import { lazy, Suspense } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { WalletPageContext } from '@/constants/chains';

// Lazy-load WalletConnectionDropdown — it imports @sodax/wallet-sdk-react (~8MB)
// and @reown/appkit hooks which should not block the initial navbar/page render.
const WalletConnectionDropdown = lazy(() => import('../common/WalletConnectionDropdown'));

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { path: '/bridge', label: 'Bridge', icon: '/images/navbar/bridge.svg' },
    // { path: '/money-market', label: 'Market', icon: '/images/navbar/market.svg' },
    { path: '/swap', label: 'Swap', icon: '/images/navbar/swap.svg' }
  ];

  const isBridgePage = location.pathname === '/bridge' || location.pathname.startsWith('/bridge/');
  const isSwapPage = location.pathname === '/swap' || location.pathname.startsWith('/swap/');
  const isLegalPage = ['/faq', '/privacy', '/terms-and-conditions'].some((p) => location.pathname === p);

  const pageContext: WalletPageContext = isBridgePage ? 'bridge' : isSwapPage ? 'swap' : 'money-market';

  return (
    <nav className="fixed left-4 right-4 top-3 z-50 h-14 sm:left-6 sm:right-6 md:left-8 md:right-8 lg:left-12 lg:right-12 xl:left-16 xl:right-16">
      <div className="flex h-full items-center justify-between px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
        {/* Logo */}
        <NavLink to="/" className="flex shrink-0 items-center gap-2 transition-opacity hover:opacity-80">
          <img src="/images/veru-logo.svg" alt="VeruFi" width="72" height="24" className="h-6 w-auto sm:h-7" />
        </NavLink>

        {/* Navigation - Center pill tabs (hidden on legal pages) */}
        {!isLegalPage && (
          <div className="absolute left-1/2 flex -translate-x-1/2 items-center">
            <div className="flex h-9 items-center gap-0.5 rounded-xl bg-white/[0.03] p-1 sm:h-11">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path || location.pathname.startsWith(link.path + '/');
                return (
                  <button
                    key={link.path}
                    onClick={() => navigate(link.path)}
                    className={`flex h-full items-center gap-1 rounded-lg px-2.5 font-jakarta text-xs font-medium transition-all duration-300 ease-in-out sm:gap-2 sm:px-4 sm:text-sm md:px-6 ${
                      isActive ? 'bg-white/[0.08] text-primary shadow-sm' : 'text-secondary hover:text-primary'
                    }`}
                  >
                    <img
                      src={link.icon}
                      alt={link.label}
                      className={`h-3.5 w-3.5 transition-opacity duration-300 sm:h-4 sm:w-4 ${isActive ? 'opacity-90' : 'opacity-40'}`}
                    />
                    <span className="hidden sm:inline">{link.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Right Side — Wallet Dropdown (hidden on legal pages) */}
        {!isLegalPage && (
          <div className="flex items-center gap-2 sm:gap-3">
            <Suspense fallback={<div className="h-9 w-28 animate-pulse rounded-lg bg-white/[0.05]" />}>
              <WalletConnectionDropdown pageContext={pageContext} />
            </Suspense>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
